<template>
  <div id="underwriting-tools-page">
    <div class="flex-grow my-4">
      <h1>{{ $t('UnderwritingTools') }}</h1>
    </div>
    <div class="vx-card full-page">
      <grid-panel
          v-if="!loading"
          class="underwriting-tools-grid"
          :column-defs="columnDefs"
          :url="url"
          @download="download"
          :apiParams="apiParams"
          :gridOptions="gridOptions"
          :autoGroupColumnDef="autoGroupColumnDef"
          :show-sidebar="false"
          :defaultColDef="defaultColDef"
          :headerTextWrap="true"
          :tooltipShowDelay="0"
          :rowHeight="47"
          :auto-size="true"
      >
        <template slot="right">
          <vs-button
              color="secondary"
              class="ml-4 text-lg"
              @click="getDownloadAllFile"
          >
            {{ $t('DownloadAllOfflineRaters') }}
          </vs-button>
        </template>
      </grid-panel>
    </div>
    <download-all
        v-if="showDownloadPopup"
        :downloading="downloadingAll"
        :success="downloadAllSuccess"
        :download-file="downloadFile"
        @downloadAll="downloadAll"
        @closeDownloadAllModal="closeDownloadAllModal"
    />

    <pdf-reader
      v-model="showPdfReader"
      :pdfReaderSrc="pdfReaderSrc"
    />
  </div>
</template>

<script>
import GridPanel from '@/views/components/ag-grid/GridPanel';
import GridActionsMenu from "@/views/components/extra-components/GridActionsMenu";
import DateRangePicker from "@/views/components/calendar/DateRangePicker";
import CellRendererDate from "@/views/components/ag-grid/cell-renderer/CellRendererDate";
import CellRendererDownloadBorder from "@/views/components/ag-grid/cell-renderer/CellRendererDownloadBorder";
import CellRendererQuickGuideButton from "@/views/components/ag-grid/cell-renderer/CellRendererQuickGuideButton";
import CellRendererCurrency from "@/views/components/ag-grid/cell-renderer/CellRendererCurrency";
import CellRendererOfflineOnline from '@/views/components/ag-grid/cell-renderer/CellRendererOfflineOnline';
import CellRendererDropdownList from '@/views/components/ag-grid/cell-renderer/CellRendererDropdownList';
import CellRendererLogo from '@/views/components/ag-grid/cell-renderer/CellRendererLogo';
import FileDownloadMixin from "@/components/mixins/FileDownloadMixin";
import SetGridOptionsMixin from "@/components/mixins/SetGridOptionsMixin";
import DownloadAll from './DownloadAll';
import ToolTipRenderer from '@/views/components/ag-grid/components/ToolTipRenderer';
import PdfReader from '@/views/components/pdfReader/PdfReader.vue';
import { TABLE_IDENTIFIER_FW_UNDERWRITING_TOOLS_ID } from '@/constants'

export default {
  components: {
    PdfReader,
    GridPanel,
    GridActionsMenu,
    DateRangePicker,
    CellRendererDate,
    CellRendererDownloadBorder,
    CellRendererQuickGuideButton,
    CellRendererCurrency,
    CellRendererOfflineOnline,
    DownloadAll,
    ToolTipRenderer,
    CellRendererDropdownList,
    CellRendererLogo,
  },
  mixins: [FileDownloadMixin, SetGridOptionsMixin],
  data() {
    return {
      url: (params) => this.$api.underwritingTools.list(params),
      startDate: null,
      endDate: null,
      documentTypes: null,
      usePdfPreview: true,
      gridOptions: {},
      languageApiParams: { language: this.$i18n.locale },
      autoGroupColumnDef: {
        headerName: '',
        cellRendererParams: { suppressCount: true },
        minWidth: 80,
        maxWidth: 80,
        colSpan: (params) => {
          if (!params.data) {
            return 4; // This should span all columns
          }

          return 1;
        },
      },
      defaultColDef: {
        resizable: true,
        sortable: true,
        wrapText: true,
        autoHeight: true,
        tooltipComponent: 'ToolTipRenderer',
      },
      showDownloadPopup: false,
      downloadingAll: false,
      downloadAllSuccess: false,
      downloadFile: {},
      tableIdentifier: TABLE_IDENTIFIER_FW_UNDERWRITING_TOOLS_ID,
      loading: false,
    };
  },
  computed: {
    apiParams() {
      return this.languageApiParams;
    },
    columnDefs() {
      if (!this.documentTypes) {
        return [];
      }

      let columnDefs = [
        {
          headerName: 'Risk Class',
          field: 'RiskClass',
          rowGroup: true,
          hide: true,
          cellRenderer: 'CellRendererQuickGuideButton',
          cellRendererParams(params) {
            return {
              documentType: 'salesDocument',
            };
          },
        },
        {
          headerName: 'Sub Risk Class',
          field: 'SubRiskClass',
          rowGroup: true,
          hide: true,
        },
      ];

      let offlineRaterCol = {};
      let onlineRaterCol = {};

      const dynamicColumns = this.documentTypes
          .filter((col) => {
            if (col.Document_Type === 'Offline Rater') {
              offlineRaterCol = col;
              return false;
            }

            if (col.Document_Type === 'Online Rater') {
              onlineRaterCol = col;
              return false;
            }

            return true;
          })
          .map(({ ID, Document_Type, DisplayName, Icon, IconType, IconSize, IconColour, IconBackground, IconBorder }) => {
            return {
              field: 'DocumentType_' + ID,
              icon: Icon,
              iconType: IconType,
              iconColour: IconColour,
              iconBackground: IconBackground,
              iconSize: IconSize,
              iconBorder: IconBorder,
              documentType: 'salesDocument',
              documentTypeId: ID,
              displayName: DisplayName,
            };
          });

      columnDefs.push({
        headerName: 'InsuranceSolutions',
        field: 'Product_Name',
        tooltipField: 'Product_Name',
        tooltipComponentParams: { field: 'Product_Name' },
        cellRenderer: 'CellRendererLogo',
        cellRendererParams(params) {
          return {
            logo: params.data.CompanyLogo,
            content: params.data.Product_Name,
          }
        },
        minWidth: 400,
        wrapText: false,
        autoHeight: false,
      });

      columnDefs.push({
        headerName: 'Raters',
        tooltipField: 'Raters',
        tooltipComponentParams: { field: 'Raters' },
        minWidth: 150,
        cellRenderer: 'CellRendererOfflineOnline',
        headerClass: 'header-center header-text-wrap',
        cellClass: 'flex justify-end fullwidth-cell',
        cellRendererParams(params) {
          return {
            offlineRaterCol,
            onlineRaterCol,
          }
        },
      });


      columnDefs.push({
        headerName: '',
        tooltipField: 'Dropdown',
        tooltipComponentParams: { field: 'Dropdown' },
        skipTranslation: true,
        minWidth: 100,
        sortable: false,
        resizable: true,
        cellRenderer: 'CellRendererDropdownList',
        headerClass: 'header-center header-text-wrap',
        cellClass: 'flex justify-end',
        cellRendererParams(params) {
          return {
            dropdownOptions: dynamicColumns
          }
        },
      });

      console.log(columnDefs);

      return columnDefs;
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async download(params) {
      try {
        if (params.classDocumentDownload) {
          return  await this.downloadClassDocument(params)
        }

        const file = await this.getDownloadInfo(params)
        this.getFile(file)

      } catch (e) {
        console.log(e);
      }
    },
    async downloadClassDocument(params) {
      try {
        const { data } = await this.$api.underwritingTools.getProductHelpDocument(params.documentGuid[0]);

        this.getFile({
          DocumentDownloadUrl: data.data.DocumentDownloadUrl,
          FriendlyName: data.data.FriendlyName,
          Extension: data.data.Extension,
        });

      } catch (e) {
        console.log(e);
      }
    },
    closeDownloadAllModal() {
      this.showDownloadPopup = false;
      this.downloadingAll = false;
      this.downloadAllSuccess = false;
      this.downloadFile = {};
    },
    async fetchData() {
      await this.getDocumentTypes();
    },
    async getDocumentTypes() {
      try {
        const { data } = await this.$api.documentTypes.list({params: this.languageApiParams});
        this.documentTypes = data.data;
      } catch (e) {
        this.$vs.notify({
          title: this.$t('ErrorOccurred'),
          text: this.$t('DocumentTypesFetchFailureMessage'),
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right',
        });
      }
    },
    getCategoryLabel(category) {
      return category.RiskClass;
    },
    getSubTabs(category) {
      if (!category.Sub_RiskClasses) {
        return category
      }

      return category.Sub_RiskClasses;
    },
    handleDownloadData(data) {
      const params = data.data;
      return this.download({ params} );
    }
  }
};
</script>

<style lang="scss">
#underwriting-tools-page {
  .underwriting-tools-grid {
    .ag-header {
      border-bottom: 0px !important
    }

    .ag-group-value {
      width: 100%;
    }

    .ag-theme-material {
      .fullwidth-cell {
        .ag-cell-wrapper {
          width: 100%;
        }
      }

      .ag-cell-wrapper {
        &.ag-cell-expandable.ag-row-group {
          display: flex;
          align-items: center;
        }
      }

      .ag-row {
        background-color: white !important;
        border-top: 2px solid white;
        border-bottom: 2px solid white;
        border-radius: 8px;

        &.ag-row-level-0 {
          background-color: #EDEDED !important;
          font-size: 28px;

          &.ag-row-hover::before {
            background-color: rgba(var(--vs-secondary),0.3) !important;
          }

          &.ag-row-hover.ag-row-selected::before,
          &.ag-row-selected::before {
            background-color:inherit !important;
            background-image: none !important;
          }

          &.ag-row-group-expanded {
            background-color: rgba(var(--vs-secondary),1) !important;
          }

          .ag-cell {
            line-height: 42px;
          }

          .ag-icon-tree-closed,
          .ag-icon-tree-open {
            font-size: 28px;
          }
        }

        &.ag-row-level-1 {
          background-color: #EDEDED !important;
          font-size: 18px;
          font-style: normal;
          font-weight: bold;

          .ag-cell {
            line-height: 42px;
          }

          .ag-icon-tree-closed,
          .ag-icon-tree-open {
            font-size: 18px;
            font-style: normal;
          }
        }

        &.ag-row-level-2 {
          padding-inline: 10px;
          font-size: 14px;
          border-top: 2px solid white;
          border-bottom: 2px solid white;
          background-color: white !important;

          &.ag-row-hover {
            > div {
              border-color: var(--ag-row-hover-border-color) !important;
            }
          }

          > div {
            border: none;
            border-top: 2px solid #EDEDED;
            border-bottom: 2px solid #EDEDED;
            background-color: white !important;
            margin-block: 2px;

            &:first-child {
              margin-left: 2px;
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
              border-left: 2px solid #EDEDED;
            }

            &:last-child {
              margin-right: 2px;
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
              border-right: 2px solid #EDEDED;
            }
          }

          .ag-cell-value {
            display: flex;
            justify-content: flex-start;
            align-items: center;
          }

          .ag-icon-tree-closed,
          .ag-icon-tree-open {
            font-size: 14px;
          }
        }
      }
    }
  }
}
</style>
